// InsiderTrading.js - Insider Trading Monitor (SEC Form 4)
import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader,
  AlertTriangle,
  UserCheck,
  DollarSign,
  Search,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Info,
  ExternalLink,
  ChevronDown
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const InsiderTrading = () => {
  const [activeTab, setActiveTab] = useState('recent');
  const [trades, setTrades] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [searchSymbol, setSearchSymbol] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDays, setFilterDays] = useState(30);
  const [minValue, setMinValue] = useState(0);

  useEffect(() => {
    setPage(1);
    setTrades([]);
    fetchData(1, true);
  }, [filterType, filterDays, minValue]);

  const fetchData = async (pageNum = 1, reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    setError(null);

    try {
      const [tradesRes, summaryRes] = await Promise.all([
        fetch(`${API_BASE}/insider/trades?page=${pageNum}&limit=50&type=${filterType}&days=${filterDays}&minValue=${minValue}`),
        reset ? fetch(`${API_BASE}/insider/summary?days=${filterDays}`) : Promise.resolve(null)
      ]);

      if (tradesRes.ok) {
        const data = await tradesRes.json();
        if (reset) {
          setTrades(data.trades || []);
        } else {
          setTrades(prev => [...prev, ...(data.trades || [])]);
        }
        setHasMore(data.hasMore || false);
        setTotalCount(data.totalCount || 0);
        setPage(data.page || 1);
      }

      if (summaryRes && summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }

    } catch (err) {
      console.error('Error fetching insider data:', err);
      setError('Failed to load insider trading data');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const searchBySymbol = async () => {
    if (!searchSymbol.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/insider/stock/${searchSymbol.toUpperCase()}`);
      if (res.ok) {
        const data = await res.json();
        setTrades(data.trades || []);
        setTotalCount(data.trades?.length || 0);
        setHasMore(false);
      }
    } catch (err) {
      setError('Failed to search symbol');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await fetch(`${API_BASE}/insider/trades?page=${nextPage}&limit=50&type=${filterType}&days=${filterDays}&minValue=${minValue}`);
      if (res.ok) {
        const data = await res.json();
        setTrades(prev => [...prev, ...(data.trades || [])]);
        setHasMore(data.hasMore || false);
        setPage(nextPage);
      }
    } catch (err) {
      console.error('Error loading more:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const formatValue = (value) => {
    if (!value || value === 0) return '—';
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatShares = (shares) => {
    if (!shares || shares === 0) return '—';
    if (shares >= 1000000) return `${(shares / 1000000).toFixed(2)}M`;
    if (shares >= 1000) return `${(shares / 1000).toFixed(1)}K`;
    return shares.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDirectionColor = (direction) => {
    if (direction === 'buy') return '#22c55e';
    if (direction === 'sell') return '#ef4444';
    return '#94a3b8';
  };

  const getDirectionLabel = (direction) => {
    if (direction === 'buy') return 'BUY';
    if (direction === 'sell') return 'SELL';
    if (direction === 'exercise') return 'EXERCISE';
    if (direction === 'gift') return 'GIFT';
    return 'OTHER';
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #065f46 0%, #059669 50%, #34d399 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-30px',
          right: '-30px',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <UserCheck size={28} />
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Insider Trading Monitor</h1>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '0.9rem' }}>
                Track CEO, CFO & director stock transactions • SEC Form 4 filings
              </p>
            </div>
            <button
              onClick={() => fetchData(1, true)}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              {loading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <Info size={20} color="#4ade80" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ color: '#86efac', fontSize: '0.85rem', margin: '0 0 0.5rem 0', fontWeight: '600' }}>
            Why Track Insider Trades?
          </p>
          <p style={{ color: '#bbf7d0', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
            Company executives must report stock trades within 2 days via SEC Form 4. 
            Insider <strong>buying</strong> often signals confidence, while selling may be routine diversification.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Total Buys</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#22c55e' }}>{summary.overview?.totalBuys || 0}</div>
            <div style={{ fontSize: '0.7rem', color: '#86efac' }}>{formatValue(summary.overview?.buyValue || 0)}</div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Total Sells</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>{summary.overview?.totalSells || 0}</div>
            <div style={{ fontSize: '0.7rem', color: '#fca5a5' }}>{formatValue(summary.overview?.sellValue || 0)}</div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Unique Stocks</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#60a5fa' }}>{summary.overview?.uniqueStocks || 0}</div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Unique Insiders</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#a78bfa' }}>{summary.overview?.uniqueInsiders || 0}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto' }}>
        {[
          { id: 'recent', label: 'Recent Trades', icon: BarChart3 },
          { id: 'top-buys', label: 'Top Buys', icon: TrendingUp },
          { id: 'top-sells', label: 'Top Sells', icon: TrendingDown }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: activeTab === tab.id ? 'rgba(34, 197, 94, 0.2)' : 'rgba(30, 41, 59, 0.5)',
              border: activeTab === tab.id ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid #334155',
              borderRadius: '0.5rem',
              color: activeTab === tab.id ? '#4ade80' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '0.75rem',
        border: '1px solid #334155'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Search Ticker</label>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <input
              type="text"
              value={searchSymbol}
              onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && searchBySymbol()}
              placeholder="AAPL"
              style={{
                flex: 1,
                padding: '0.5rem',
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.25rem',
                color: 'white',
                fontSize: '0.85rem'
              }}
            />
            <button onClick={searchBySymbol} style={{ padding: '0.5rem', background: '#059669', border: 'none', borderRadius: '0.25rem', color: 'white', cursor: 'pointer' }}>
              <Search size={16} />
            </button>
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Type</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', background: 'rgba(15, 23, 42, 0.5)', border: '1px solid #334155', borderRadius: '0.25rem', color: 'white', fontSize: '0.85rem' }}
          >
            <option value="all">All Trades</option>
            <option value="buy">Buys Only</option>
            <option value="sell">Sells Only</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Time Period</label>
          <select
            value={filterDays}
            onChange={(e) => setFilterDays(parseInt(e.target.value))}
            style={{ width: '100%', padding: '0.5rem', background: 'rgba(15, 23, 42, 0.5)', border: '1px solid #334155', borderRadius: '0.25rem', color: 'white', fontSize: '0.85rem' }}
          >
            <option value={7}>7 Days</option>
            <option value={30}>30 Days</option>
            <option value={90}>90 Days</option>
            <option value={180}>180 Days</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Min Value</label>
          <select
            value={minValue}
            onChange={(e) => setMinValue(parseInt(e.target.value))}
            style={{ width: '100%', padding: '0.5rem', background: 'rgba(15, 23, 42, 0.5)', border: '1px solid #334155', borderRadius: '0.25rem', color: 'white', fontSize: '0.85rem' }}
          >
            <option value={0}>Any</option>
            <option value={10000}>$10K+</option>
            <option value={100000}>$100K+</option>
            <option value={1000000}>$1M+</option>
            <option value={10000000}>$10M+</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Loader size={48} color="#22c55e" className="spin" />
        </div>
      ) : error ? (
        <div style={{ padding: '2rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.75rem', textAlign: 'center' }}>
          <p style={{ color: '#fca5a5' }}>{error}</p>
          <button onClick={() => fetchData(1, true)} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#22c55e', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      ) : (
        <>
          {/* Recent Trades Tab */}
          {activeTab === 'recent' && (
            <div>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Showing {trades.length} of {totalCount} trades
              </p>

              <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', border: '1px solid #334155', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Date</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Symbol</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Insider</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>Type</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>Shares</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>Price</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>Value</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>SEC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trades.map((trade, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>{formatDate(trade.transactionDate)}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#60a5fa' }}>{trade.symbol !== '--' ? trade.symbol : '—'}</span>
                            {trade.symbol === '--' && trade.companyName && (
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trade.companyName}</div>
                            )}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <div style={{ fontWeight: '500', fontSize: '0.85rem' }}>{trade.insiderName}</div>
                            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{trade.insiderTitle}</div>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              background: `${getDirectionColor(trade.tradeDirection)}20`,
                              color: getDirectionColor(trade.tradeDirection),
                              fontSize: '0.7rem',
                              fontWeight: '600'
                            }}>
                              {trade.tradeDirection === 'buy' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                              {getDirectionLabel(trade.tradeDirection)}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.85rem' }}>{formatShares(trade.shares)}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.85rem' }}>${trade.price?.toFixed(2) || '0.00'}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.85rem', fontWeight: '600', color: getDirectionColor(trade.tradeDirection) }}>
                            {formatValue(trade.value)}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            {trade.link && (
                              <a href={trade.link} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>
                                <ExternalLink size={14} />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {hasMore && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 2rem',
                      background: 'rgba(34, 197, 94, 0.2)',
                      border: '1px solid rgba(34, 197, 94, 0.4)',
                      borderRadius: '0.5rem',
                      color: '#4ade80',
                      cursor: loadingMore ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}
                  >
                    {loadingMore ? <Loader size={18} className="spin" /> : <ChevronDown size={18} />}
                    {loadingMore ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Top Buys Tab */}
          {activeTab === 'top-buys' && summary?.topBought && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {summary.topBought.map((stock, idx) => (
                <div key={idx} style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '0.75rem',
                  padding: '1.25rem',
                  border: '1px solid #334155',
                  borderLeft: '4px solid #22c55e'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#60a5fa' }}>{stock.symbol}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{stock.count} insider buys</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: '#22c55e' }}>{formatValue(stock.value)}</div>
                      <div style={{ fontSize: '0.7rem', color: '#86efac' }}>Total value</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                    {stock.insiders} unique insider{stock.insiders > 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Top Sells Tab */}
          {activeTab === 'top-sells' && summary?.topSold && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {summary.topSold.map((stock, idx) => (
                <div key={idx} style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '0.75rem',
                  padding: '1.25rem',
                  border: '1px solid #334155',
                  borderLeft: '4px solid #ef4444'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#60a5fa' }}>{stock.symbol}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{stock.count} insider sells</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: '#ef4444' }}>{formatValue(stock.value)}</div>
                      <div style={{ fontSize: '0.7rem', color: '#fca5a5' }}>Total value</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                    {stock.insiders} unique insider{stock.insiders > 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Disclaimer */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '0.75rem',
        marginTop: '1.5rem'
      }}>
        <AlertTriangle size={20} color="#f87171" style={{ flexShrink: 0, marginTop: '2px' }} />
        <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
          <strong>Disclaimer:</strong> Insider transactions are not investment advice. 
          Selling may be for personal reasons (diversification, taxes, etc.) unrelated to company prospects. 
          Always do your own research.
        </p>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default InsiderTrading;